import { Project } from "@model/Project";
import {
  Component,
  OnInit,
  Input,
  ViewChild,
  AfterViewInit,
} from "@angular/core";
import { FormGroup, Validators, FormBuilder } from "@angular/forms";
import { ProjectService } from "@service/project.service";
import * as moment from "moment";
import * as is from "is";
import { Message } from "@model/Message";
import { ToastrService } from "ngx-toastr";
import { PeriodicService, Periodic } from "@service/periodics.service";
import { concat, Observable, of, Subject } from "rxjs";
import {
  distinctUntilChanged,
  switchMap,
  tap,
  catchError,
} from "rxjs/operators";
import { AuthService } from "@service/auth.service";
import { TemplateFormComponent } from "@component/template-form/template-form.component";
import { Events } from "@service/events.service";
import { Template } from "@model/Template";
import { UsersService } from "@service/users.service";
import { TemplateService } from "@service/templates.service";
import { ModalOpenaiDialogComponent } from "@component/modal-openai-dialog/modal-openai-dialog.component";
import {
  NgbModal,
  NgbModalOptions,
  NgbActiveModal,
  NgbDate,
} from "@ng-bootstrap/ng-bootstrap";
import { JoyrideService } from "ngx-joyride";
import { JoyrideSteps } from "@model/JoyrideSteps";

@Component({
  selector: "app-modal-project",
  templateUrl: "./modal-project.component.html",
  styleUrls: ["./modal-project.component.scss"],
})
export class ModalProjectComponent implements OnInit, AfterViewInit {
  @ViewChild("templateForm", { static: false })
  public templateForm: TemplateFormComponent;
  @ViewChild("openaiDialog", { static: false })
  public openaiDialog: ModalOpenaiDialogComponent;
  @Input() public project: Project = {} as Project;
  @Input() private readonly repositoryId: number;
  public hasCollapsed: boolean = false;

  projectForm: FormGroup;
  submitted: boolean = false;
  public references_type: any[] = [
    {
      id: "abnt",
      name: "ABNT - Associação Brasileira de Normas Técnicas (Português - Brasil)",
    },
    {
      id: "vancouver_brackets_quote",
      name: "Vancouver (citação em colchetes)",
    },
    {
      id: "vancouver_superscript_quote",
      name: "Vancouver (citação em sobrescrito)",
    },
    { id: "apa", name: "APA - American Psychological Association" },
  ];

  periodic$: Observable<Periodic[]>;
  periodicLoading: boolean = false;
  periodicInput$ = new Subject<string>();
  selectedPeriodics: Periodic[] = [];
  modalOptions: NgbModalOptions;

  public projectId: number = null;
  public nameAffiliation: string;
  public selectTipoVisible: boolean = true;
  public templates: Array<Template> = [];
  public templateSelection = "";

  constructor(
    private formBuilder: FormBuilder,
    private projectService: ProjectService,
    private periodicService: PeriodicService,
    private authService: AuthService,
    private events: Events,
    private templateService: TemplateService,
    public activeModal: NgbActiveModal,
    private toast: ToastrService,
    private modalService: NgbModal,
    private readonly joyrideService: JoyrideService
  ) {}

  ngOnInit() {
    this.nameAffiliation =
      this.authService.user.student_institution.info.affiliation.name;

    this.projectForm = this.formBuilder.group({
      title: [
        this.project.title,
        [Validators.required, Validators.minLength(10)],
      ],
      forecast: [this.project.forecast_date, Validators.required],
      description: [this.project.description],
      referenceType: [this.project.reference_type, Validators.required],
    });

    if (!is.empty(this.project.id)) {
      const forecast = moment(this.project.forecast_date, "MM-DD-YYYY");
      this.project.forecast = {
        year: forecast.year(),
        month: forecast.month() + 1,
        day: forecast.date(),
      };
      this.projectForm.get("forecast").setValue(this.project.forecast);
      this.selectedPeriodics = this.project.periodics;
    }
    if (!this.project.id) {
      this.projectForm.get("referenceType").setValue("abnt");
    }

    this.loadPeriodics();
    this.templateService
      .listTemplatesByAuthor()
      .subscribe((templates: Array<Template>) => {
        this.templates = templates;
      });
  }

  callOpenAiModal() {
    const modalRef = this.modalService.open(
      ModalOpenaiDialogComponent,
      this.modalOptions
    );

    modalRef.result.catch(() => {
      this.activeModal.dismiss();
    });

    modalRef.componentInstance.valueSender.subscribe((res) => {
      this.projectForm.get("title").setValue(res);
    });
  }

  trackByFn(item: Periodic) {
    return item.id;
  }

  private loadPeriodics() {
    this.periodic$ = concat(
      of([]), // default items
      this.periodicInput$.pipe(
        distinctUntilChanged(),
        tap(() => (this.periodicLoading = true)),
        switchMap((term) =>
          this.periodicService.getPeriodics(term).pipe(
            catchError(() => of([])), // empty list on error
            tap(() => (this.periodicLoading = false))
          )
        )
      )
    );
  }

  save() {
    this.submitted = true;

    if (this.projectForm.invalid) {
      return;
    }

    let forecast = this.projectForm.get("forecast").value;
    this.project.forecast_date = `${forecast.year}-${forecast.month}-${forecast.day}`;
    this.project.title = this.projectForm.get("title").value;
    this.project.description = this.projectForm.get("description").value;
    this.project.reference_type = this.projectForm.get("referenceType").value;
    this.project.periodics = this.selectedPeriodics;

    this.submitted = false;

    if (this.project.id) {
      this.projectService.update(this.project.id, this.project).subscribe(
        (project: Project) => {
          this.toast.success(Message.SUCCESS);
          this.project.forecast_date = `${forecast.month}-${forecast.day}-${forecast.year}`;
          this.activeModal.close(this.project);
        },
        (handler) => {
          console.log(handler);
        }
      );
    } else {
      this.project.status = "development";
      this.projectService.store(this.project, this.repositoryId).subscribe(
        (project: Project) => {
          const template = this.templates.find(
            (temp) => temp.id === +this.templateSelection
          );
          if (template) {
            this.applyTemplate(template, project);
          } else {
            this.templateForm.projectId = project.id;
            this.templateForm.saveTemplate();
          }
          this.events.on("templateAdded").subscribe(({ template }) => {
            this.applyTemplate(template, project);
          });
        },
        (handler) => {
          console.log(handler);
        }
      );
    }
  }

  private applyTemplate(template, project): void {
    this.templateService
      .applyTemplate({ template_id: template.id }, project.id)
      .subscribe((res) => {
        ProjectService.setTemplate(null);
        if (res.document) {
          project.template = template;
        }
        this.toast.success(Message.SUCCESS);
        this.activeModal.close(project);
      });
  }

  expandClick(element: HTMLElement) {
    if (!element.classList.contains("collapsed")) {
      this.hasCollapsed = false;
    } else {
      this.hasCollapsed = true;
    }
  }

  hasTemplates(): boolean {
    return this.templates.length > 0;
  }

  handleTemplateSelection() {}

  ngAfterViewInit() {
    const joyrideSteps: JoyrideSteps = JSON.parse(
      localStorage.getItem("joyrideSteps")
    );
    if (
      typeof joyrideSteps.thirdStep === "undefined" ||
      joyrideSteps.thirdStep === false
    ) {
      this.joyrideService
        .startTour({
          steps: ["firstStep"],
          customTexts: {
            next: "Próximo",
            prev: "Anterior",
            done: "Ok",
          },
        })
        .subscribe(() => {
          localStorage.setItem(
            "joyrideSteps",
            JSON.stringify({ ...joyrideSteps, thirdStep: true })
          );
        });
    }
  }
}
